import type { ComponentType } from 'react'
import { POS_REPORTS } from '@iws/backoffice-api'
import {
    Activity,
    Award,
    Banknote,
    Clock,
    TrendingUp,
    BarChart3,
    Blocks,
    Braces,
    CalendarClock,
    CreditCard,
    FileText,
    Globe,
    House,
    Inbox,
    Languages,
    MapPin,
    Megaphone,
    MessageSquare,
    Monitor,
    PackageCheck,
    Receipt,
    Settings,
    Shield,
    ShieldAlert,
    Store,
    Tag,
    Tags,
    Truck,
    UserRound,
    UsersRound,
} from 'lucide-react'

export type SidebarItemType = 'link' | 'group' | 'header' | 'plugins'

export interface SidebarItem {
    type: SidebarItemType
    label?: string
    to?: string
    icon?: any
    /**
     * Hidden unless every listed flag is enabled (a dot path, e.g. `orders.all_orders`). Storefront-only pages also
     * list `storefront`, the channel flag, so POS-only tenants do not see them (POS.md O4).
     */
    featureFlag?: string | string[]
    /**
     * Hidden unless the staff member holds this permission (what the page's main list endpoint requires). Feature flags
     * say whether the business has the function; this says whether this staff member may use it. A list = any one.
     */
    permission?: string | readonly string[]
    id?: string // For collapsible state key
    /** Rendered in the pinned block at the bottom of the sidebar (Settings). */
    placement?: 'bottom'
    /** Settings nav only: shown indented under the sibling with this `to` (e.g. delivery methods under shipping). */
    parentTo?: string
    children?: SidebarItem[]
}

/*
 * Admin information architecture, grouped under short section labels (Sell, Grow, Store, Apps). A group's first
 * visible child is its landing page: the parent row links there and the remaining children show as sub-items while
 * the group is active. Routes and feature flags are unchanged (docs/ADMIN_SHOPIFY_REDESIGN_PLAN.md §4).
 */
export const sidebarConfig: SidebarItem[] = [
    {
        type: 'link',
        to: '/',
        label: 'Home',
        icon: House,
        featureFlag: 'dashboard',
    },
    { type: 'header', label: 'Sell' },
    {
        type: 'group',
        id: 'orders',
        label: 'Orders',
        icon: Inbox,
        children: [
            { type: 'link', to: '/orders', label: 'Orders', featureFlag: ['orders', 'orders.all_orders'], permission: 'orders:list' },
            { type: 'link', to: '/orders/sources', label: 'Order sources', featureFlag: ['orders', 'orders.source_list'], permission: 'order_sources:list' },
            { type: 'link', to: '/orders/notes', label: 'Order notes', featureFlag: ['orders', 'orders.note_list'], permission: 'order_notes:list' },
            { type: 'link', to: '/returns', label: 'Returns', featureFlag: ['storefront', 'returns'], permission: 'orders:list' },
        ],
    },
    {
        type: 'group',
        id: 'products',
        label: 'Products',
        icon: Tag,
        children: [
            { type: 'link', to: '/products', label: 'Products', featureFlag: ['products', 'products.all_products'], permission: 'products:list' },
            { type: 'link', to: '/categories', label: 'Categories', featureFlag: ['products', 'products.categories'], permission: 'categories:list' },
            { type: 'link', to: '/brands', label: 'Brands', featureFlag: ['products', 'products.brands'], permission: 'brands:list' },
            { type: 'link', to: '/attributes', label: 'Attributes', featureFlag: ['products', 'products.attributes'], permission: 'product_attributes:list' },
            { type: 'link', to: '/inventory', label: 'Inventory', featureFlag: 'inventory', permission: 'stocks:list' },
            { type: 'link', to: '/suppliers', label: 'Suppliers', featureFlag: 'inventory', permission: 'suppliers:list' },
            { type: 'link', to: '/units', label: 'Measurement units', featureFlag: ['products', 'pos'], permission: 'measurement_units:list' },
            { type: 'link', to: '/reviews', label: 'Reviews', featureFlag: ['storefront', 'reviews'], permission: 'reviews:list' },
        ],
    },
    {
        type: 'link',
        to: '/customers',
        label: 'Customers',
        icon: UserRound,
        featureFlag: 'customers',
        permission: 'customers:list',
    },
    { type: 'header', label: 'Grow' },
    {
        type: 'group',
        id: 'marketing',
        label: 'Marketing',
        icon: Megaphone,
        children: [
            { type: 'link', to: '/campaigns', label: 'Campaigns', featureFlag: 'campaigns', permission: 'offers:list' },
            { type: 'link', to: '/marketing', label: 'Email marketing', featureFlag: 'marketing' },
        ],
    },
    {
        type: 'link',
        to: '/coupons',
        label: 'Coupons',
        icon: Tags,
        featureFlag: 'coupons',
        permission: 'coupons:list',
    },
    {
        type: 'group',
        id: 'analytics',
        label: 'Analytics',
        icon: BarChart3,
        children: [
            { type: 'link', to: '/analytics', label: 'Analytics', permission: 'analytics:overview' },
            { type: 'link', to: '/reports/sales', label: 'Sales report', permission: 'reports:orders' },
            { type: 'link', to: '/reports/revenue', label: 'Revenue report', permission: 'reports:orders' },
            { type: 'link', to: '/reports/customers', label: 'Customer report', permission: 'reports:customers' },
            { type: 'link', to: '/reports/inventory', label: 'Inventory report', permission: 'reports:inventory' },
        ],
    },
    { type: 'header', label: 'Store' },
    {
        type: 'link',
        to: '/themes',
        label: 'Online store',
        icon: Store,
        featureFlag: ['storefront', 'themes'],
    },
    // The cross-branch side of the POS (POS.md §18.4); the till itself is pos-web.
    {
        type: 'group',
        id: 'pos',
        label: 'Point of sale',
        icon: Monitor,
        children: [
            { type: 'link', to: '/pos/orders', label: 'Orders', icon: Receipt, featureFlag: 'pos', permission: 'pos:list' },
            { type: 'link', to: '/pos/branches', label: 'Branches', icon: Store, featureFlag: 'pos', permission: 'branches:list' },
            { type: 'link', to: '/pos/terminals', label: 'Terminals', icon: Monitor, featureFlag: 'pos', permission: 'terminals:list' },
            { type: 'link', to: '/pos/shifts', label: 'Shifts', icon: Clock, featureFlag: 'pos', permission: 'pos:open_shift' },
            { type: 'link', to: '/pos/cash', label: 'Cash in and out', icon: Banknote, featureFlag: 'pos', permission: 'pos:cash_in' },
            { type: 'link', to: '/pos/credit-repayments', label: 'Credit repayments', icon: CreditCard, featureFlag: 'pos', permission: 'pos_reports:credit_repay' },
            { type: 'link', to: '/pos/shift-report', label: 'Shift report', icon: FileText, featureFlag: 'pos', permission: 'pos_reports:shifts' },
            { type: 'link', to: '/pos/profit-loss', label: 'Profit and loss', icon: TrendingUp, featureFlag: 'pos', permission: 'pos:view_profit_loss' },
            { type: 'link', to: '/pos/reports', label: 'POS reports', icon: BarChart3, featureFlag: 'pos', permission: POS_REPORTS.map((r) => r.permission) },
            { type: 'link', to: '/pos/price-books', label: 'Price books', icon: Tags, featureFlag: 'pos', permission: 'price_books:list' },
            { type: 'link', to: '/pos/membership-tiers', label: 'Membership tiers', icon: Award, featureFlag: 'pos', permission: 'membership_tiers:list' },
            { type: 'link', to: '/pos/offers', label: 'Offers', icon: Tag, featureFlag: ['pos', 'campaigns'], permission: 'offers:list' },
            // Settings the POS apps read (was the POS tab of /settings).
            { type: 'link', to: '/pos/settings', label: 'Settings', icon: Settings, featureFlag: 'pos' },
        ],
    },
    {
        type: 'group',
        id: 'content',
        label: 'Content',
        icon: FileText,
        children: [
            { type: 'link', to: '/blog', label: 'Blog posts', featureFlag: ['storefront', 'blog'], permission: 'blogs:list' },
            { type: 'link', to: '/media', label: 'Files', featureFlag: 'media.media_library', permission: 'media:list' },
        ],
    },
    { type: 'header', label: 'Apps' },
    {
        type: 'link',
        to: '/plugins',
        label: 'Plugins',
        icon: Blocks,
        featureFlag: 'plugins',
    },
    // Installed plugins' nav items are inserted here.
    {
        type: 'plugins',
    },
    {
        type: 'group',
        id: 'settings',
        label: 'Settings',
        icon: Settings,
        placement: 'bottom',
        children: [
            { type: 'link', to: '/settings', label: 'General', icon: Store, featureFlag: 'settings' },
            { type: 'link', to: '/settings/subscription', label: 'Subscription', icon: CalendarClock, permission: 'renewals:list' },
            { type: 'link', to: '/shipping', label: 'Shipping and delivery', icon: Truck, featureFlag: ['storefront', 'shipping'] },
            { type: 'link', to: '/orders/methods', label: 'Delivery methods', icon: PackageCheck, featureFlag: ['storefront', 'orders.delivery_methods'], parentTo: '/shipping', permission: 'delivery_methods:list' },
            { type: 'link', to: '/orders/locations', label: 'Delivery areas', icon: MapPin, featureFlag: ['storefront', 'orders', 'locations'], parentTo: '/shipping', permission: 'delivery_location_types:list' },
            { type: 'link', to: '/taxes', label: 'Taxes', icon: Receipt, featureFlag: 'taxes' },
            { type: 'link', to: '/system/payment-gateways', label: 'Payments', icon: CreditCard, featureFlag: ['storefront', 'system.payment_gateways'], permission: 'payment_gateways:list' },
            { type: 'link', to: '/system/sms-gateways', label: 'SMS', icon: MessageSquare, featureFlag: 'system.sms_gateways', permission: 'sms_gateways:list' },
            { type: 'link', to: '/system/administration', label: 'Users', icon: UsersRound, featureFlag: 'administration.admin', permission: 'admins:list' },
            { type: 'link', to: '/system/roles', label: 'Roles and permissions', icon: Shield, featureFlag: 'administration.admin', permission: 'roles:list' },
            { type: 'link', to: '/system/ip-blocklist', label: 'Security', icon: ShieldAlert, featureFlag: 'system.blocklist', permission: 'ip_blocks:list' },
            { type: 'link', to: '/system/activity-log', label: 'Activity log', icon: Activity, featureFlag: 'system.activity_log', permission: 'activity_sessions:list' },
        ],
    },
]

/**
 * The `/settings` page's tabs, listed as their own items in the settings nav (§6.10). The page selects the tab from
 * `?tab=`; `value` must match its `TabsContent` values.
 */
export const settingsTabs = [
    { value: 'general', label: 'General', icon: Store },
    { value: 'localization', label: 'Localization', icon: Languages },
    { value: 'domains', label: 'Domains', icon: Globe },
    { value: 'datalayer', label: 'Data layer', icon: Braces },
] as const

export interface SettingsPage {
    to: string
    label: string
    icon?: ComponentType<{ className?: string }>
    /** Shown indented under its parent page (delivery methods under shipping). */
    nested: boolean
}

/**
 * The settings group's pages as every settings menu lists them (the settings column, the rail flyout and the full
 * sidebar): `/settings` expanded into its tabs, sub-pages nested. Pass the group's children after feature flags.
 */
export const settingsPages = (children: SidebarItem[]): SettingsPage[] =>
    children.flatMap((c): SettingsPage[] => {
        if (!c.to) return []
        if (c.to === '/settings') {
            return settingsTabs.map((t) => ({
                to: t.value === settingsTabs[0].value ? '/settings' : `/settings?tab=${t.value}`,
                label: t.label,
                icon: t.icon,
                nested: false,
            }))
        }
        // Indented only under a parent that is shown; a hidden parent would leave it indented under nothing.
        const nested = !!c.parentTo && children.some((p) => p.to === c.parentTo)
        return [{ to: c.to, label: c.label ?? '', icon: c.icon, nested }]
    })
